<script setup lang="ts">
import Drawer from 'primevue/drawer'
import type { MenuItem } from 'primevue/menuitem'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useViteEnvironment } from '@/shared/composables/useViteEnvironment'
import MenuSettings from '@/features/dashboard/components/MenuSettings.vue'

const props = defineProps<{
  items: MenuItem[]
}>()

const { isDev, isTest } = useViteEnvironment()
const router = useRouter()
const userAuth = useAuthStore()
const { user } = storeToRefs(userAuth)

const visible = ref(false)

/** Loose entries (e.g. Homepage) render before the grouped subcategory sections. */
const looseItems = computed(() => props.items.filter(item => !item.items))
const groups = computed(() => props.items.filter(item => item.items?.length))
</script>

<template>
  <header class="h-14 flex shrink-0 items-center gap-3 border-b border-surface-100 bg-surface-0 px-4">
    <span class="relative h-10 w-10 flex shrink-0 items-center justify-center rounded-lg bg-primary text-sm text-surface-0 font-bold">
      S4W
    </span>
    <div class="min-w-0 flex flex-1 items-center gap-x-2">
      <div class="min-w-0 flex flex-col leading-tight">
        <span class="whitespace-nowrap text-base color-primary font-bold">Swipe4Work</span>
        <span class="whitespace-nowrap text-xs color-surface-400 font-semibold tracking-wide uppercase">Admin</span>
      </div>
      <Tag
        v-if="isDev || isTest"
        severity="danger" :value="isDev ? 'DEV' : 'TEST'" class="p-1" :pt="{
          label: {
            class: '!text-xs font-bold',
          },
        }"
      />
    </div>

    <Button
      class="ml-auto"
      text
      rounded
      severity="secondary"
      icon="fa-regular fa-bars"
      aria-label="Navigatiemenu openen"
      :aria-expanded="visible"
      aria-controls="mobile-nav"
      @click="visible = true"
    />
  </header>

  <!-- Bottom sheet: slides up from the bottom, scrollable list, closes on item click. -->
  <Drawer
    v-model:visible="visible"
    position="bottom"
    class="max-h-[85dvh] !h-auto !rounded-t-xl"
    :pt="{
      header: { class: 'relative !py-3' },
      content: { class: '!px-2 !pb-2' },
    }"
  >
    <template #header>
      <span class="absolute left-1/2 top-1.5 h-1 w-10 rounded-full bg-surface-200 -translate-x-1/2" aria-hidden="true" />
      <div class="flex items-center gap-x-2">
        <span class="h-10 w-10 flex shrink-0 items-center justify-center rounded-lg bg-primary text-sm text-surface-0 font-bold">S4W</span>
        <div class="min-w-0 flex flex-1 items-center gap-x-2">
          <div class="min-w-0 flex flex-col leading-tight">
            <span class="whitespace-nowrap text-base color-primary font-bold">Swipe4Work</span>
            <span class="whitespace-nowrap text-xs color-surface-400 font-semibold tracking-wide uppercase">Admin</span>
          </div>
          <Tag
            v-if="isDev || isTest"
            severity="danger" :value="isDev ? 'DEV' : 'TEST'" class="p-1" :pt="{
              label: {
                class: '!text-xs font-bold',
              },
            }"
          />
        </div>
      </div>
    </template>

    <nav id="mobile-nav" class="flex flex-col">
      <template v-for="section in [{ label: null, items: looseItems }, ...groups.map(g => ({ label: g.label, items: g.items as MenuItem[] }))]" :key="section.label ?? 'loose'">
        <span
          v-if="section.label"
          class="px-3 pb-1 pt-4 text-xs text-surface-400 font-semibold tracking-wide uppercase"
        >
          {{ section.label }}
        </span>

        <template v-for="item in section.items" :key="item.label">
          <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a
              v-ripple
              :href="href"
              class="flex items-center gap-3 rounded-md px-3 py-3 color-surface-700 no-underline"
              @click="(event) => {
                if (event.ctrlKey || event.metaKey) return;
                event.preventDefault();
                navigate();
                visible = false;
              }"
            >
              <i :class="item.icon" class="w-6 shrink-0 text-center text-lg" />
              <span class="min-w-0 flex-1 truncate text-sm font-medium">{{ item.label }}</span>
            </a>
          </router-link>
          <a
            v-else
            v-ripple
            :href="item.url"
            :target="item.target"
            class="flex items-center gap-3 rounded-md px-3 py-3 color-surface-700 no-underline"
            @click="visible = false"
          >
            <i :class="item.icon" class="w-6 shrink-0 text-center text-lg" />
            <span class="min-w-0 flex-1 truncate text-sm font-medium">{{ item.label }}</span>
          </a>
        </template>
      </template>

      <div class="mt-4 flex items-center gap-x-3 rounded-md bg-background p-2">
        <span
          class="flex shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 p-2"
          role="button"
          aria-label="Uitloggen"
          @click="userAuth.logout().then(() => router.push({ name: 'auth.login' }))"
        >
          <i class="fa-regular fa-user" />
        </span>
        <div class="min-w-0 flex flex-1 flex-col gap-y-0 color-primary">
          <span class="truncate font-bold">{{ user?.name }}</span>
          <span class="truncate text-xs">{{ user?.username }}</span>
        </div>
        <MenuSettings compact />
      </div>
    </nav>
  </Drawer>
</template>
